import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { ArrowLeft, Copy, Share2, Check, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { subscribeToCircle, updateCircleName, removeMember, leaveCircle } from '../services/circleService';
import AvatarIcon from '../components/AvatarIcon';

export default function CircleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [circle, setCircle] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!id) return;
    return subscribeToCircle(id, setCircle);
  }, [id]);

  if (!circle) {
    return <Loading>Loading...</Loading>;
  }

  const isAdmin = circle.memberInfo?.[currentUser?.uid]?.role === 'admin';

  const handleSaveName = async () => {
    if (newName.trim() && newName.trim() !== circle.name) {
      await updateCircleName(id, newName.trim());
    }
    setEditingName(false);
  };

  const handleCopyCode = async () => {
    await navigator.clipboard.writeText(circle.inviteCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    const inviteUrl = `${window.location.origin}/join/${circle.inviteCode}`;
    const text = `Join my Art Ping Pong circle "${circle.name}"!\n${inviteUrl}`;
    if (navigator.share) {
      await navigator.share({ title: 'Join Art Ping Pong', text, url: inviteUrl });
    } else {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (window.confirm('Remove this member from the circle?')) {
      await removeMember(id, memberId);
    }
  };

  const handleLeave = async () => {
    if (window.confirm('Leave this circle? You can rejoin with the invite code.')) {
      await leaveCircle(id, currentUser.uid);
      navigate('/circles');
    }
  };

  return (
    <Container>
      <Header>
        <BackBtn onClick={() => navigate('/circles')}>
          <ArrowLeft size={20} />
        </BackBtn>
        <HeaderTitle>Circle Settings</HeaderTitle>
        <Spacer />
      </Header>

      <Section>
        <SectionTitle>Circle Name</SectionTitle>
        {editingName ? (
          <EditRow>
            <EditInput
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
              autoFocus
            />
            <SaveBtn onClick={handleSaveName}>Save</SaveBtn>
          </EditRow>
        ) : (
          <NameRow>
            <NameText>{circle.name}</NameText>
            {isAdmin && (
              <EditBtn onClick={() => { setNewName(circle.name); setEditingName(true); }}>
                Edit
              </EditBtn>
            )}
          </NameRow>
        )}
      </Section>

      <Section>
        <SectionTitle>Invite Code</SectionTitle>
        <CodeRow>
          <InviteCode>{circle.inviteCode}</InviteCode>
          <CodeActions>
            <SmallBtn onClick={handleCopyCode}>
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </SmallBtn>
            <SmallBtn onClick={handleShare}>
              <Share2 size={16} />
            </SmallBtn>
          </CodeActions>
        </CodeRow>
      </Section>

      <Section>
        <SectionTitle>Members ({circle.memberIds?.length || 0})</SectionTitle>
        <MemberList>
          {circle.memberIds?.map((memberId) => {
            const info = circle.memberInfo?.[memberId];
            const isSelf = memberId === currentUser?.uid;
            return (
              <MemberRow key={memberId}>
                <AvatarIcon name={info?.displayName} photoURL={info?.photoURL} size={40} />
                <MemberInfo>
                  <MemberName>
                    {info?.displayName || 'Member'}
                    {isSelf && <YouBadge>You</YouBadge>}
                  </MemberName>
                  <MemberRole>{info?.role || 'member'}</MemberRole>
                </MemberInfo>
                {isAdmin && !isSelf && (
                  <RemoveBtn onClick={() => handleRemoveMember(memberId)}>
                    <Trash2 size={16} />
                  </RemoveBtn>
                )}
              </MemberRow>
            );
          })}
        </MemberList>
      </Section>

      {!isAdmin && (
        <LeaveButton onClick={handleLeave}>
          Leave Circle
        </LeaveButton>
      )}
    </Container>
  );
}

const Container = styled.div`
  padding: 0 16px 32px;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
`;

const BackBtn = styled.button`
  padding: 8px;
  border-radius: ${({ theme }) => theme.radius.small};
  color: ${({ theme }) => theme.colors.textSecondary};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
`;

const HeaderTitle = styled.h1`
  font-size: 18px;
  font-weight: 700;
  flex: 1;
`;

const Spacer = styled.div`
  width: 36px;
`;

const Section = styled.section`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textTertiary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.surfaceSecondary};
`;

const NameText = styled.span`
  font-size: 16px;
  font-weight: 600;
`;

const EditBtn = styled.button`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.accent};
  font-weight: 500;
`;

const EditRow = styled.div`
  display: flex;
  gap: 8px;
`;

const EditInput = styled.input`
  flex: 1;
  padding: 10px 14px;
  border-radius: ${({ theme }) => theme.radius.medium};
  border: 1px solid ${({ theme }) => theme.colors.accent};
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  font-size: 16px;
  outline: none;
`;

const SaveBtn = styled.button`
  padding: 10px 20px;
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.accent};
  color: white;
  font-weight: 600;
`;

const CodeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.surfaceSecondary};
`;

const InviteCode = styled.span`
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 3px;
  color: ${({ theme }) => theme.colors.accent};
  font-family: 'SF Mono', 'Menlo', monospace;
`;

const CodeActions = styled.div`
  display: flex;
  gap: 4px;
`;

const SmallBtn = styled.button`
  padding: 8px;
  border-radius: ${({ theme }) => theme.radius.small};
  color: ${({ theme }) => theme.colors.textSecondary};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
`;

const MemberList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MemberRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.medium};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
  }
`;

const MemberInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const MemberName = styled.div`
  font-size: 15px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const YouBadge = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.accent};
  background: rgba(245, 158, 11, 0.1);
  padding: 1px 6px;
  border-radius: ${({ theme }) => theme.radius.full};
`;

const MemberRole = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textTertiary};
  text-transform: capitalize;
`;

const RemoveBtn = styled.button`
  padding: 8px;
  border-radius: ${({ theme }) => theme.radius.small};
  color: ${({ theme }) => theme.colors.error};

  &:hover {
    background: rgba(239, 68, 68, 0.05);
  }
`;

const LeaveButton = styled.button`
  width: 100%;
  padding: 14px;
  border-radius: ${({ theme }) => theme.radius.medium};
  color: ${({ theme }) => theme.colors.error};
  font-size: 15px;
  font-weight: 500;
  border: 1px solid ${({ theme }) => theme.colors.border};
  transition: all 0.2s;
  margin-top: 16px;

  &:hover {
    background: rgba(239, 68, 68, 0.05);
    border-color: ${({ theme }) => theme.colors.error};
  }
`;

const Loading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;
